/**
 * locationMediaService.js
 *
 * Loads locationMediaConfig.json at runtime and provides fast sweep-to-media lookup.
 *
 * Usage:
 *   locationMediaService.fetchAndLoadConfig(url)   — fetch JSON from public/config/
 *   locationMediaService.getMediaForSweep(sweepId) — returns location entry or null
 */

class LocationMediaService {
  constructor() {
    this._sweepMap = new Map(); // sweepId → location entry
    this._allLocations = [];    // all locations that have media (in config order)
    this._loaded = false;
  }

  async fetchAndLoadConfig(url) {
    try {
      const res = await fetch(url + '?t=' + Date.now());
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const config = await res.json();

      this._sweepMap.clear();
      this._allLocations = [];
      for (const loc of (config.locations || [])) {
        // Only register locations that actually have media
        if (!loc.media || loc.media.length === 0) continue;
        this._allLocations.push(loc);
        for (const sid of (loc.sweepIds || [])) {
          this._sweepMap.set(sid, loc);
        }
      }
      this._loaded = true;
      console.log(`📸 LocationMediaService: loaded ${this._allLocations.length} locations, ${this._sweepMap.size} sweep entries`);
    } catch (err) {
      console.warn('⚠️ LocationMediaService: Could not load config:', err.message);
    }
  }

  /** Returns true once the config has been fetched and parsed. */
  isReady() {
    return this._loaded;
  }

  /** Returns all locations that have at least one media item, in config order. */
  getAllLocations() {
    return this._allLocations;
  }

  /**
   * Returns the location object (with .media, .title, .id) for the given sweep ID,
   * or null if no media is configured for that sweep.
   */
  getMediaForSweep(sweepId) {
    if (!sweepId || !this._loaded) return null;
    return this._sweepMap.get(sweepId) || null;
  }
}

const locationMediaService = new LocationMediaService();
export default locationMediaService;
